import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, RefreshCw, Loader2, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const TradingHistory = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [positions, setPositions] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(searchParams.get('days') || '7');
  const [selectedExchange, setSelectedExchange] = useState(searchParams.get('exchange') || 'all');
  const [connections, setConnections] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'closed_at', direction: 'desc' });
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      fetchConnections();
    }
  }, [user]);

  useEffect(() => {
    if (user && connections.length > 0) {
      fetchTradingHistory();
    }
  }, [user, selectedPeriod, selectedExchange, connections]);

  // Auto-sync positions on first load
  useEffect(() => {
    if (user && connections.length > 0 && positions.length === 0 && !loadingData) {
      // Sync positions automatically on first visit
      handleSync(true); // true = silent mode
    }
  }, [connections]);

  const fetchConnections = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/exchanges/connections`, {
        withCredentials: true
      });
      setConnections(response.data);
    } catch (error) {
      console.error('Error fetching connections:', error);
    }
  };

  const fetchTradingHistory = async () => {
    setLoadingData(true);
    try {
      const params = new URLSearchParams();
      
      if (selectedExchange !== 'all') {
        params.append('exchange_name', selectedExchange);
      }
      
      if (selectedPeriod !== 'all') {
        params.append('days', selectedPeriod);
      }

      const response = await axios.get(
        `${backendUrl}/api/exchanges/trading-history?${params.toString()}`,
        { withCredentials: true }
      );

      setPositions(response.data.positions || []);
      setStatistics(response.data.statistics || null);
    } catch (error) {
      console.error('Error fetching trading history:', error);
      toast.error('Ошибка загрузки истории');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSync = async (silent = false) => {
    if (connections.length === 0) {
      if (!silent) toast.error('Нет подключенных бирж');
      return;
    }

    setSyncing(true);
    let totalSynced = 0;

    try {
      for (const conn of connections) {
        try {
          const response = await axios.post(
            `${backendUrl}/api/exchanges/sync-positions?exchange_name=${conn.exchange_name}`,
            {},
            { withCredentials: true }
          );
          totalSynced += response.data.synced_count || 0;
        } catch (error) {
          console.error(`Error syncing ${conn.exchange_name}:`, error);
        }
      }

      if (!silent) {
        if (totalSynced > 0) {
          toast.success(`Синхронизировано ${totalSynced} новых позиций`);
        } else {
          toast.info('Нет новых позиций для синхронизации');
        }
      }
      
      // Refresh history after sync
      if (totalSynced > 0) {
        fetchTradingHistory();
      }
    } catch (error) {
      if (!silent) toast.error('Ошибка синхронизации');
    } finally {
      setSyncing(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedPositions = React.useMemo(() => {
    let sorted = [...positions];
    
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        // Handle dates
        if (sortConfig.key === 'closed_at' || sortConfig.key === 'opened_at') {
          aVal = new Date(aVal).getTime();
          bVal = new Date(bVal).getTime();
        }
        
        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return sorted;
  }, [positions, sortConfig]);

  const handlePeriodChange = (days) => {
    setSelectedPeriod(days);
    const params = new URLSearchParams(searchParams);
    if (days === 'all') {
      params.delete('days');
    } else {
      params.set('days', days);
    }
    setSearchParams(params);
  };

  const handleExchangeChange = (exchange) => {
    setSelectedExchange(exchange);
    const params = new URLSearchParams(searchParams);
    if (exchange === 'all') {
      params.delete('exchange');
    } else {
      params.set('exchange', exchange);
    }
    setSearchParams(params);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Header />
        <div className="pt-20 pb-8 px-4 md:px-6 max-w-7xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-800 rounded w-1/4"></div>
            <div className="h-64 bg-gray-800 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Toaster position="top-right" />
      <Header />
      
      <div className="pt-20 pb-8 px-4 md:px-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">История сделок</h1>
          <p className="text-gray-400">Закрытые позиции на фьючерсах</p>
        </div>

        {/* Filters and Statistics */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Statistics Cards */}
          {statistics && (
            <>
              <Card className="bg-[#1a1a1a] border-gray-800">
                <CardContent className="pt-6">
                  <div className="text-sm text-gray-400 mb-1">Всего сделок</div>
                  <div className="text-3xl font-bold text-white">{statistics.total_count}</div>
                  <div className="text-sm text-gray-500 mt-2">
                    <span className="text-green-400">{statistics.profitable_count} прибыльных</span>
                    {' · '}
                    <span className="text-red-400">{statistics.losing_count} убыточных</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#1a1a1a] border-gray-800">
                <CardContent className="pt-6">
                  <div className="text-sm text-gray-400 mb-1">Процент побед</div>
                  <div className={`text-3xl font-bold ${statistics.win_rate >= 50 ? 'text-green-400' : 'text-orange-400'}`}>
                    {statistics.win_rate}%
                  </div>
                </CardContent>
              </Card>

              <Card className={`${statistics.total_pnl >= 0 ? 'bg-green-900/20 border-green-800' : 'bg-red-900/20 border-red-800'}`}>
                <CardContent className="pt-6">
                  <div className="text-sm text-gray-400 mb-1">Общий PnL</div>
                  <div className={`text-3xl font-bold ${statistics.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {statistics.total_pnl >= 0 ? '+' : ''}{statistics.total_pnl} USDT
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Filters */}
        <Card className="bg-[#1a1a1a] border-gray-800 mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              {/* Period Filter */}
              <div className="flex flex-wrap gap-2">
                <span className="text-gray-400 text-sm mr-2 flex items-center">Период:</span>
                {[
                  { value: '1', label: '24ч' },
                  { value: '7', label: '7 дней' },
                  { value: '30', label: '30 дней' },
                  { value: '90', label: '90 дней' },
                  { value: 'all', label: 'Все время' }
                ].map((period) => (
                  <Button
                    key={period.value}
                    size="sm"
                    variant={selectedPeriod === period.value ? 'default' : 'outline'}
                    onClick={() => handlePeriodChange(period.value)}
                    className={
                      selectedPeriod === period.value
                        ? 'bg-[#00ff88] text-black hover:bg-[#00cc6f]'
                        : 'border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800'
                    }
                  >
                    {period.label}
                  </Button>
                ))}
              </div>

              {/* Exchange Filter */}
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-gray-400 text-sm mr-2">Биржа:</span>
                <Button
                  size="sm"
                  variant={selectedExchange === 'all' ? 'default' : 'outline'}
                  onClick={() => handleExchangeChange('all')}
                  className={
                    selectedExchange === 'all'
                      ? 'bg-[#00ff88] text-black hover:bg-[#00cc6f]'
                      : 'border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800'
                  }
                >
                  Все
                </Button>
                {connections.map((conn) => (
                  <Button
                    key={conn.exchange_name}
                    size="sm"
                    variant={selectedExchange === conn.exchange_name ? 'default' : 'outline'}
                    onClick={() => handleExchangeChange(conn.exchange_name)}
                    className={
                      selectedExchange === conn.exchange_name
                        ? 'bg-[#00ff88] text-black hover:bg-[#00cc6f]'
                        : 'border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800'
                    }
                  >
                    {conn.exchange_name.toUpperCase()}
                  </Button>
                ))}
              </div>

              {/* Sync Button */}
              <Button
                onClick={handleSync}
                disabled={syncing}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {syncing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Синхронизация...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Синхронизировать
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Trading History Cards */}
        <div className="space-y-4">
          {loadingData ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 mx-auto animate-spin text-gray-400" />
              <p className="text-gray-400 mt-4">Загрузка истории...</p>
            </div>
          ) : sortedPositions.length === 0 ? (
            <Card className="bg-[#1a1a1a] border-gray-800">
              <CardContent className="py-12 text-center text-gray-400">
                <p className="text-lg mb-2">Нет данных</p>
                <p className="text-sm">Нажмите "Синхронизировать" чтобы загрузить историю с биржи</p>
              </CardContent>
            </Card>
          ) : (
            sortedPositions.map((position, index) => {
              const isProfitable = position.pnl_usdt > 0;
              const closedDate = new Date(position.closed_at);
              const openedDate = new Date(position.opened_at);
              
              return (
                <Card 
                  key={position.id || index} 
                  className={`border-l-4 ${
                    isProfitable 
                      ? 'bg-green-900/10 border-green-500' 
                      : 'bg-red-900/10 border-red-500'
                  } hover:bg-opacity-80 transition-all`}
                >
                  <CardContent className="p-4">
                    {/* Header: Symbol and Status */}
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-3">
                        {/* Symbol Badge */}
                        <div className={`px-3 py-1 rounded ${
                          isProfitable ? 'bg-green-500' : 'bg-red-500'
                        }`}>
                          <span className="text-white font-bold text-sm">S</span>
                        </div>
                        
                        {/* Symbol and Type */}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-bold text-lg">{position.symbol}</span>
                            <Badge variant="outline" className="text-xs">
                              {position.side === 'long' ? 'Long' : 'Short'}
                            </Badge>
                            <Badge variant="outline" className="text-xs capitalize">
                              {position.exchange_name}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      
                      {/* Status Badge */}
                      <Badge 
                        className={`${
                          isProfitable 
                            ? 'bg-green-500/20 text-green-400 border-green-500'
                            : 'bg-red-500/20 text-red-400 border-red-500'
                        }`}
                      >
                        Closed
                      </Badge>
                    </div>
                    
                    {/* PNL and ROI Row */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-gray-400 text-sm mb-1">Closing PNL (USDT)</p>
                        <p className={`text-2xl font-bold ${
                          isProfitable ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {isProfitable ? '+' : ''}{position.pnl_usdt.toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm mb-1">ROI</p>
                        <p className={`text-2xl font-bold ${
                          isProfitable ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {isProfitable ? '+' : ''}{position.pnl_percent.toFixed(2)}%
                        </p>
                      </div>
                    </div>
                    
                    {/* Prices Row */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-gray-400 text-sm mb-1">Entry Price</p>
                        <p className="text-white font-semibold">
                          {position.entry_price.toLocaleString(undefined, { 
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2 
                          })}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm mb-1">Avg. Close Price</p>
                        <p className="text-white font-semibold">
                          {position.exit_price.toLocaleString(undefined, { 
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2 
                          })}
                        </p>
                      </div>
                    </div>
                    
                    {/* Position Size Row */}
                    <div className="mb-4">
                      <p className="text-gray-400 text-sm mb-1">Closed Vol. (USD)</p>
                      <p className="text-white font-semibold">
                        {position.position_size_usd.toLocaleString(undefined, { 
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2 
                        })}
                      </p>
                    </div>
                    
                    {/* Dates Row */}
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-500">
                      <div>
                        <span>Opened: </span>
                        <span className="text-gray-400">
                          {openedDate.toLocaleString('ru-RU', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <div>
                        <span>Closed: </span>
                        <span className="text-gray-400">
                          {closedDate.toLocaleString('ru-RU', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default TradingHistory;
